import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import GoogleButton from "../components/GoogleButton";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const defaultRole = params.get("role") === "organizer" ? "organizer" : "attendee";
  const [form, setForm] = useState({ name: "", email: "", password: "", role: defaultRole });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome to Afterhours, ${res.user.name}`);
      nav(res.user.role === "organizer" ? "/organizer" : "/events");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-md items-center px-4 py-10">
      <div className="w-full rounded-3xl border border-zinc-800 bg-[#3A1020] p-8 glow-amber">
        <div className="mb-6">
          <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Create account</div>
          <h1 className="font-display text-3xl font-black uppercase text-zinc-50">Join Afterhours</h1>
          <p className="mt-2 text-sm text-zinc-400">Two clicks to your first ticket.</p>
        </div>

        <div className="mb-5 grid grid-cols-2 gap-2" data-testid="role-picker">
          {[
            { v: "attendee", l: "I'm here to attend" },
            { v: "organizer", l: "I'm hosting events" },
          ].map((r) => (
            <button
              key={r.v}
              data-testid={`role-${r.v}`}
              type="button"
              onClick={() => setForm({ ...form, role: r.v })}
              className={`rounded-xl border p-3 text-left text-xs transition ${form.role === r.v ? "border-amber-500 bg-amber-500/10 text-amber-300" : "border-zinc-800 bg-[#2F0D18] text-zinc-300 hover:border-amber-500/40"}`}
            >
              <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400">{r.v}</div>
              <div className="mt-1 font-medium">{r.l}</div>
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Name</Label>
            <Input data-testid="register-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1 border-zinc-800 bg-[#2F0D18] text-zinc-100" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Email</Label>
            <Input data-testid="register-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1 border-zinc-800 bg-[#2F0D18] text-zinc-100" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Password</Label>
            <Input data-testid="register-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} className="mt-1 border-zinc-800 bg-[#2F0D18] text-zinc-100" />
          </div>
          {form.role === "organizer" && <p data-testid="organizer-kyc-note" className="text-[11px] text-zinc-500">You'll verify your PAN and Instagram inside the organizer console before publishing your first event.</p>}
          <Button data-testid="register-submit" type="submit" disabled={loading} className="h-11 w-full bg-amber-500 font-bold uppercase tracking-wider text-zinc-950 hover:bg-amber-400">
            {loading ? "Creating..." : "Create account"}
          </Button>
        </form>

        <div className="my-5 flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500"><span className="h-px flex-1 bg-zinc-800" />or<span className="h-px flex-1 bg-zinc-800" /></div>
        <GoogleButton role={form.role} label={`Sign up with Google as ${form.role}`} />

        <div className="mt-6 text-center text-sm text-zinc-400">
          Already have one? <Link to="/login" className="font-semibold text-amber-400 hover:text-amber-300">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
